import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getOutlets } from './api';
import { useAuth } from './AuthContext';
import logger from './logger';

const OutletContext = createContext(null);

const OUTLET_KEY = 'selected_outlet_id';

export const useOutlet = () => {
  const context = useContext(OutletContext);
  if (!context) {
    throw new Error('useOutlet must be used within OutletProvider');
  }
  return context;
};

/**
 * Resolve the outlet_id to use based on user context
 */
function resolveInitialOutlet(user) {
  // 1. Check if user has current_outlet_id
  if (user?.current_outlet_id) {
    logger.info('[Outlet] Using current_outlet_id:', user.current_outlet_id);
    return user.current_outlet_id;
  }
  
  // 2. Check outlet_ids array (skip wildcard)
  const outletIds = user?.outlet_ids || [];
  if (outletIds.length > 0 && outletIds[0] !== '*') {
    logger.info('[Outlet] Using first outlet_id:', outletIds[0]);
    return outletIds[0];
  }
  
  // 3. Check localStorage for previously selected outlet
  const stored = localStorage.getItem(OUTLET_KEY);
  if (stored) {
    logger.info('[Outlet] Using stored outlet_id:', stored);
    return stored;
  }
  
  // 4. Super admin/wildcard - need to select
  logger.info('[Outlet] No default outlet - selection required');
  return null;
}

export const OutletProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsSelection, setNeedsSelection] = useState(false);

  // Check if user needs to select an outlet (super admin / wildcard)
  const userNeedsOutletSelection = useCallback(() => {
    if (!user) return false;
    
    // Super admin with no default outlet
    if (user.is_super_admin && !user.current_outlet_id) {
      return true;
    }
    
    // Wildcard access with no default outlet
    const outletIds = user.outlet_ids || [];
    if (outletIds.includes('*') && !user.current_outlet_id) {
      return true;
    }
    
    return false;
  }, [user]);

  // Load outlets and resolve initial selection
  const loadOutlets = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      logger.info('[Outlet] Loading outlets for user:', user.email);
      
      // Try to resolve outlet from user context first
      const resolvedOutlet = resolveInitialOutlet(user);
      
      if (resolvedOutlet) {
        setSelectedOutlet(resolvedOutlet);
        localStorage.setItem(OUTLET_KEY, resolvedOutlet);
        setNeedsSelection(false);
      } else if (userNeedsOutletSelection()) {
        // Need to fetch outlets for selection
        logger.info('[Outlet] User needs to select an outlet');
        try {
          const response = await getOutlets();
          setOutlets(response.outlets || []);
          
          // If only one outlet available, auto-select it
          if (response.outlets?.length === 1) {
            const onlyOutlet = response.outlets[0].outlet_id || response.outlets[0].id;
            logger.info('[Outlet] Auto-selecting only available outlet:', onlyOutlet);
            setSelectedOutlet(onlyOutlet);
            localStorage.setItem(OUTLET_KEY, onlyOutlet);
            setNeedsSelection(false);
          } else {
            setNeedsSelection(true);
          }
        } catch (err) {
          logger.error('[Outlet] Failed to fetch outlets:', err);
          // Allow super admin to proceed without outlet list
          if (user.is_super_admin) {
            setNeedsSelection(true);
          }
        }
      }
    } catch (err) {
      logger.error('[Outlet] Error loading outlets:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, userNeedsOutletSelection]);

  useEffect(() => {
    loadOutlets();
  }, [loadOutlets]);

  // Select an outlet
  const selectOutlet = useCallback((outletId) => {
    setSelectedOutlet(outletId);
    localStorage.setItem(OUTLET_KEY, outletId);
    setNeedsSelection(false);
  }, []);

  // Clear outlet selection
  const clearOutlet = useCallback(() => {
    setSelectedOutlet(null);
    localStorage.removeItem(OUTLET_KEY);
    setNeedsSelection(userNeedsOutletSelection());
  }, [userNeedsOutletSelection]);

  const outletContextValue = useMemo(() => ({
    selectedOutlet,
    outlets,
    loading,
    needsSelection,
    selectOutlet,
    clearOutlet,
    refreshOutlets: loadOutlets
  }), [selectedOutlet, outlets, loading, needsSelection, selectOutlet, clearOutlet, loadOutlets]);

  return (
    <OutletContext.Provider value={outletContextValue}>
      {children}
    </OutletContext.Provider>
  );
};
